import { json } from "@remix-run/node";
import { Link, useLoaderData, useParams } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { useCategoryPage } from "~/api/queries/useCategoryPage";
import { useCurrentUser } from "~/api/queries/useCurrentUser";
import { studentCoursesQueryOptions } from "~/api/queries/useStudentCourses";
import { AcademiaLoader } from "~/components/AcademiaLoader";
import { Button } from "~/components/ui/button";
import { useLanguageStore } from "~/modules/Dashboard/Settings/Language/LanguageStore";
import { CATALOG_PATH } from "~/utils/catalogPath";
import { buildMeta, getCompanyFromMatches, ogImageUrl } from "~/utils/meta-helpers";
import { serverFetchOutcome, serverFetchSafe } from "~/utils/server-fetch.server";

import { PublicCourseCard } from "../components/PublicCourseCard";
import { defaultOrder } from "../lib/courseFacets";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import type { SupportedLanguages } from "@repo/shared";
import type { GetAvailableCoursesResponse, GetCategoryPageResponse } from "~/api/generated-api";

/**
 * Página pública de una categoría: `/courses/category/:slug`.
 *
 * ═══ Qué se sacó y por qué ═══
 * Era la plantilla del fork, y publicaba cosas falsas en prod y en el sitemap: "500+
 * Estudiantes", "50+ Cursos" (hay dos publicados), "24/7 Soporte", tres testimonios inventados
 * ("Maria Garcia", "Carlos Lopez", "Ana Rodriguez") y "Unite a miles de estudiantes", todo en el
 * azul del template y sin tildes. Ahora la página muestra SÓLO lo que existe: el nombre de la
 * categoría (o el título y la bajada que el admin le cargue) y sus cursos publicados, con la
 * misma tarjeta del catálogo.
 *
 * Tampoco quedan los botones "Ver todos los cursos" / "Ver cursos de X": con el catálogo oculto
 * para el lanzamiento (`CATALOG_PATH`) los dos terminaban en la home.
 */

/**
 * Loader de SERVIDOR: la página es una landing pública y tiene que salir entera en el HTML
 * (crawlers y LLMs no ejecutan JS). Dos fetch en paralelo, los mismos que las queries del
 * componente (`categoryPageQueryOptions` y `availableCoursesQueryOptions({ category })`), para
 * que servidor y primer render del cliente pinten lo mismo.
 *
 * Sin categoría el status depende de POR QUÉ falta, igual que en CourseDetail:
 *   · 4xx de la API → la categoría no tiene página pública → 404 REAL (no un soft-404 con 200).
 *     La API resuelve esta URL sólo para categorías con `showInMenu = true` y el sitemap de
 *     `server.js` filtra por el MISMO criterio.
 *   · La API no contestó → 503: un blip no puede borrar la categoría del índice.
 */
export const loader = async ({ params }: LoaderFunctionArgs) => {
  const slug = params.slug || "";
  const encoded = encodeURIComponent(slug);

  const [categoryOutcome, coursesResponse] = await Promise.all([
    serverFetchOutcome<GetCategoryPageResponse>(`/api/category/page/${encoded}`),
    serverFetchSafe<GetAvailableCoursesResponse>(
      `/api/course/available-courses?page=1&perPage=100&language=es&category=${encoded}`,
    ),
  ]);

  const courses = coursesResponse?.data ?? null;

  if (!categoryOutcome.ok) {
    return json(
      { category: null as GetCategoryPageResponse["data"] | null, courses },
      { status: categoryOutcome.reason === "not-found" ? 404 : 503 },
    );
  }

  return json({ category: categoryOutcome.data.data ?? null, courses });
};

type Localized = string | Record<string, string> | object | null | undefined;

/** Los campos de la categoría llegan como `{ es, en }` o como texto plano. */
function localized(value: Localized, language: string): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const record = value as Record<string, string>;
  return record?.[language] || record?.es || Object.values(record || {})[0] || "";
}

export const meta: MetaFunction<typeof loader> = ({ data, matches, params }) => {
  const company = getCompanyFromMatches(matches);
  const category = data?.category;

  // Título: el hero configurado desde el admin > el nombre de la categoría > el genérico.
  const displayTitle = localized(category?.heroTitle, "es") || localized(category?.title, "es");
  const subtitle = localized(category?.heroSubtitle, "es");
  // Antes la descripción salía vacía (`content=""`): sin bajada del admin, se describe con lo
  // que la página efectivamente muestra.
  const description =
    subtitle ||
    (displayTitle
      ? `Cursos de ${displayTitle} en ${company}: inteligencia artificial en español, explicada para que la entienda cualquiera.`
      : "");

  return buildMeta({
    title: displayTitle ? `${displayTitle} | ${company}` : `Categoría | ${company}`,
    description,
    image: ogImageUrl(category?.heroImageUrl),
    path: params.slug ? `/courses/category/${params.slug}` : undefined,
  });
};

export default function CategoryLandingPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { language } = useLanguageStore();

  // Siembra del loader: `ssr*` viaja en `window.__remixContext`, así que servidor y primer
  // render del cliente pintan lo mismo. Las queries ganan apenas resuelven.
  const { category: ssrCategory, courses: ssrCourses } = useLoaderData<typeof loader>();

  const { data: fetchedCategory, isLoading: isLoadingCategory } = useCategoryPage(slug);
  const category = fetchedCategory ?? ssrCategory ?? undefined;

  const { data: fetchedCourses, isLoading: isLoadingCourses } = useAvailableCourses({
    language: language as SupportedLanguages,
    ...(slug && { category: slug }),
  });
  const courses = useMemo(
    () => defaultOrder(fetchedCourses ?? ssrCourses ?? []),
    [fetchedCourses, ssrCourses],
  );

  // Mismo criterio que el catálogo: con sesión, cada tarjeta tiene que saber si el alumno ya
  // está inscripto o ya lo compró; sin sesión, los botones del anónimo son los correctos.
  const { data: currentUser } = useCurrentUser();
  const isLoggedIn = !!currentUser;
  const { data: studentCourses } = useQuery(
    studentCoursesQueryOptions({ language }, { enabled: isLoggedIn }),
  );
  const enrolledIds = useMemo(
    () => new Set((studentCourses ?? []).filter((c) => c.enrolled).map((c) => c.id)),
    [studentCourses],
  );
  const purchasedIds = useMemo(
    () =>
      new Set(
        (studentCourses ?? [])
          .filter((c) => (c as { purchased?: boolean }).purchased)
          .map((c) => c.id),
      ),
    [studentCourses],
  );

  // El loader de carga sólo si NO hay nada que mostrar.
  const isLoading = (isLoadingCategory || isLoadingCourses) && !category;

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <AcademiaLoader label={t("landing.categoryPage.loading")} />
      </div>
    );
  }

  if (!category) {
    return (
      <section className="bg-landing-cream px-5 py-24 text-center sm:px-8">
        <h1 className="font-display text-[clamp(26px,3.6vw,36px)] font-bold tracking-tight text-landing-text">
          {t("landing.categoryPage.notFound")}
        </h1>
        <Button variant="brand" className="mt-8" asChild>
          <Link to={CATALOG_PATH}>{t("landing.categoryPage.viewCourses")}</Link>
        </Button>
      </section>
    );
  }

  const title = localized(category.heroTitle, language) || localized(category.title, language);
  const subtitle = localized(category.heroSubtitle, language);
  const ctaText = localized(category.heroCtaText, language);
  const ctaUrl = category.heroCtaUrl;

  return (
    <section className="bg-landing-cream pb-20 pt-10 sm:pt-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="max-w-[720px]">
          <h1 className="font-display text-[28px] font-bold leading-[1.15] tracking-tight text-landing-text sm:text-[36px] lg:text-[48px]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 text-[16px] leading-relaxed text-landing-muted sm:text-[18px]">
              {subtitle}
            </p>
          )}
          {ctaText && ctaUrl && (
            <Button variant="brand" className="mt-6" asChild>
              <Link to={ctaUrl}>{ctaText}</Link>
            </Button>
          )}
        </header>

        {category.heroImageUrl && (
          <img
            src={category.heroImageUrl}
            alt=""
            className="mt-8 aspect-[21/9] w-full rounded-xl object-cover shadow-landing-md"
          />
        )}

        {courses.length > 0 ? (
          <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {courses.map((course, index) => {
              const courseFlags = course as { enrolled?: boolean; purchased?: boolean };
              return (
                <li key={course.id} className="flex">
                  <PublicCourseCard
                    course={course}
                    isEnrolled={enrolledIds.has(course.id) || !!courseFlags.enrolled}
                    isPurchased={purchasedIds.has(course.id) || !!courseFlags.purchased}
                    isLoggedIn={isLoggedIn}
                    priority={index < 2}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="mt-10 max-w-[560px] border-l-[3px] border-landing-red pl-4">
            <p className="text-[16px] leading-relaxed text-landing-text">
              {t("landing.categoryPage.noCoursesAvailable")}
            </p>
            <Link
              to={CATALOG_PATH}
              className="mt-2 inline-block font-semibold text-landing-red underline decoration-landing-red/30 underline-offset-4 hover:decoration-landing-red"
            >
              {t("landing.categoryPage.viewCourses")}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
